import { Box, Popover, TableCell, TableRow } from '@mui/material';
import { FC, useState } from 'react';
import { ActivitySheetEmployee } from './types';
import TooltipTableCell from '../../components/formlib/TooltipTableCell';
import ActionToolTip from '../../components/formlib/ActionToolTip';
import { ReactComponent as ViewIcon } from '../../assets/images/eye on.svg';
import { formatName } from '../../utils/nameUtils';
import { getTimeString } from '../../utils/dateUtil';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as InfoIcon } from '../../assets/images/info.svg';
import { LOCAL_DATE_TIME_FORMAT } from '../../services/Constant';
import {
  formatNumberToDecimal,
  formatNumberToDecimalString,
} from '../../utils/utilities';

interface ActivitySheetEmployeeRowProps {
  data: ActivitySheetEmployee;
  handleViewClick: (id: string) => void;
}

const ActivitySheetEmployeeRow: FC<ActivitySheetEmployeeRowProps> = ({
  data,
  handleViewClick,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [type, setType] = useState<'payroll' | 'billing' | null>(null);
  const open = Boolean(anchorEl);

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  return (
    <TableRow
      hover
      tabIndex={-1}
      sx={{
        alignContent: 'flex-start',
        '&:nth-of-type(odd)': {
          backgroundColor: '#ECF9F8',
        },
      }}
    >
      <TooltipTableCell
        value={formatName(data.firstName, null, data.lastName, null)}
        visible={true}
      />
      <TooltipTableCell
        value={data.employeeType ?? '-'}
        visible={true}
        textSxProps={{ maxWidth: '150px' }}
      />
      <TooltipTableCell
        value={
          data.billableHoursDec === null
            ? '-'
            : formatNumberToDecimal(data.billableHoursDec)
        }
        visible={false}
      />
      <TooltipTableCell
        value={
          data.otherAssocWorkHoursDec === null ||
          data.employeeType === 'Salaried Exempt'
            ? '-'
            : formatNumberToDecimal(data.otherAssocWorkHoursDec)
        }
        visible={false}
      />
      <TooltipTableCell
        value={
          data.approvedNonBillableHoursDec === null
            ? '-'
            : formatNumberToDecimal(data.approvedNonBillableHoursDec)
        }
        visible={false}
      />
      <TooltipTableCell
        value={
          data.hoursOutDec === null
            ? '-'
            : formatNumberToDecimalString(data.hoursOutDec) ?? '0.00'
        }
        visible={false}
      />
      <TooltipTableCell
        value={
          data.totalHoursDec === null
            ? '-'
            : formatNumberToDecimal(data.totalHoursDec)
        }
        visible={false}
      />
      <TooltipTableCell
        value={
          data.productivity === null ? '-' : data.productivity.toString() + '%'
        }
        visible={false}
      />
      <TableCell>
        <Box className="flex__" alignItems="center">
          <MediumTypography
            label={data.payrollStatus ?? '-'}
            className="singleLine"
          />
          {data.payrollStatus !== 'Not Submitted' && data.payrollModifiedBy && (
            <Box
              className="ml-xs"
              onMouseEnter={(e) => {
                setAnchorEl(e.currentTarget);
                setType('payroll');
              }}
              onMouseLeave={handlePopoverClose}
              style={{
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              <InfoIcon stroke="var(--thom-mono-gray)" />
              <Popover
                id="mouse-over-popover"
                sx={{
                  pointerEvents: 'none',
                }}
                open={open && type === 'payroll'}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
              >
                {data.payrollModifiedBy && (
                  <Box className="p-sm" alignItems={'center'}>
                    <MediumTypography
                      className="mainText-sm font-bold"
                      labelid="payrollModifiedByText"
                      defaultlabel="Payroll Modified By:"
                    />
                    <MediumTypography
                      className="mainText-sm"
                      label={data.payrollModifiedBy ?? ''}
                    />
                    <MediumTypography
                      className="mainText-sm"
                      label={getTimeString(
                        data.payrollModifiedOn ?? '-',
                        LOCAL_DATE_TIME_FORMAT,
                        true,
                      )}
                    />
                  </Box>
                )}
              </Popover>
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box className="flex__" alignItems="center">
          <MediumTypography
            label={data.billingStatus ?? '-'}
            className="singleLine"
          />
          {data.billingStatus !== 'Not Submitted' && data.billingModifiedBy && (
            <Box
              className="ml-xs"
              onMouseEnter={(e) => {
                setAnchorEl(e.currentTarget);
                setType('billing');
              }}
              onMouseLeave={handlePopoverClose}
              style={{
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              <InfoIcon stroke="var(--thom-mono-gray)" />
              <Popover
                id="mouse-over-popover"
                sx={{
                  pointerEvents: 'none',
                }}
                open={open && type === 'billing'}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
              >
                {data.billingModifiedBy && (
                  <Box className="p-sm" alignItems={'center'}>
                    <MediumTypography
                      className="mainText-sm font-bold"
                      labelid="billingModifiedByText"
                      defaultlabel="Billing Modified By:"
                    />
                    <MediumTypography
                      className="mainText-sm"
                      label={data.billingModifiedBy ?? ''}
                    />
                    <MediumTypography
                      className="mainText-sm"
                      label={getTimeString(
                        data.billingModifiedOn ?? '-',
                        LOCAL_DATE_TIME_FORMAT,
                        true,
                      )}
                    />
                  </Box>
                )}
              </Popover>
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <ActionToolTip labelId="viewText" defaultLabel="View">
          <Box
            sx={{
              cursor: 'pointer',
              display: 'flex',
            }}
            onClick={() => {
              handleViewClick(data.id);
            }}
          >
            <ViewIcon />
          </Box>
        </ActionToolTip>
      </TableCell>
    </TableRow>
  );
};

export default ActivitySheetEmployeeRow;
